import { COMMUNITY_TOPICS } from '@zoxo/shared';

/**
 * Site-wide constants for SEO / OG / sharing (AC-5).
 *
 * SITE_URL is the canonical public origin used to build absolute URLs for OG
 * tags and JSON-LD. On the client we prefer the live origin; on the server we
 * fall back to VITE_SITE_URL (set per deploy) or the dev origin.
 */
export const SITE_NAME = '좋소 과수원';
/**
 * 한 줄 태그라인. **길게 늘어놓지 않는다.**
 *
 * 이전 부제(「정부지원사업, 언제 얼마를 주는지부터 어떻게 타먹지까지」)는
 * 33자라 제목 태그에서 잘리고 배너 문구와도 따로 놀았다. 이 문장은 히어로
 * 배너에 이미 구워져 있는 말이라 브랜드가 한 목소리로 모인다.
 */
export const SITE_TAGLINE = '줘도 못먹나?';
/** 제목·공유 카드에 쓰는 완성형. */
export const SITE_TITLE = `${SITE_NAME} - ${SITE_TAGLINE}`;
export const SITE_DESCRIPTION = '정부지원사업 공고를 바닥까지 긁어모아 분석해, 금액까지 알려주마.';

/** Sitemap page meta description. */
export const SITEMAP_DESCRIPTION = '좋소 과수원의 정부지원사업 공고와 기사 목록.';

/** RSS index page meta description. */
export const RSS_DESCRIPTION = '좋소 과수원의 RSS 피드. 구독해 새 글을 받아보세요.';

/** The public RSS feeds listed on the /rss index page (XML endpoints). */
export const RSS_FEEDS: ReadonlyArray<{ label: string; href: string; description: string }> = [
  {
    label: '지원사업',
    href: '/news/rss.xml',
    description: '새로 올라온 정부지원사업 공고 — 보조금·바우처·융자·보증·프로그램',
  },
  {
    label: '아티클',
    href: '/articles/rss.xml',
    description: '지원사업 데이터로 쓰는 주간 기사와 분석',
  },
  {
    label: '커뮤니티 전체',
    href: '/posts/rss.xml',
    description: '모든 게시판의 새 글',
  },
  ...COMMUNITY_TOPICS.map((t) => ({
    label: `커뮤니티 · ${t.label}`,
    href: `/posts/${t.key}/rss.xml`,
    description: `「${t.label}」 게시판의 새 글`,
  })),
];

/**
 * 랜딩 히어로 배너. 1769×889 (≈2:1).
 *
 * 문구가 이미지에 구워져 있어 **검색엔진도 화면 낭독기도 못 읽는다.** 그래서
 * h1 은 실제 텍스트로 따로 두고 이 이미지는 alt 로 같은 내용을 말한다.
 */
// WebP 두 벌 — 원본 PNG(734KB) 대신 900w(134KB) · 1400w(221KB). 본문 폭 896px 이라 1400w 가 2x 에 가깝다.
export const HERO_IMAGE = 'https://cdn.zoxo.kr/heros/hero-0912-1400.webp';
export const HERO_IMAGE_SRCSET =
  'https://cdn.zoxo.kr/heros/hero-0912-900.webp 900w, https://cdn.zoxo.kr/heros/hero-0912-1400.webp 1400w';
/** 본문 폭(max-w-4xl = 896px) 이상에서는 896px, 그 아래는 화면 폭. */
export const HERO_IMAGE_SIZES = '(min-width: 896px) 896px, 100vw';
export const HERO_IMAGE_WIDTH = 1400;
export const HERO_IMAGE_HEIGHT = 704;
export const HERO_ALT =
  // alt 는 **그림에 실제로 있는 것**을 말한다. 태그라인을 짧게 바꿨어도
  // 이 이미지에는 아직 옛 문구가 구워져 있으므로 그대로 적는다. 배너를
  // 새로 만들면 이 문장도 따라가야 한다.
  '좋소 과수원 — 정부지원사업, 언제 얼마를 주는지부터 어떻게 따먹지까지. ' +
  '개나 소나 창업하는 모두의 창업 시대, 그대들의 보물지도가 되어 주리를 틀라.';

/** Default OG/Twitter preview image (1200×630). Used when a page has no image. */
export const SITE_OG_IMAGE = 'https://cdn.zoxo.kr/assets/og-alt.png';
export const SITE_OG_IMAGE_WIDTH = 1200;
export const SITE_OG_IMAGE_HEIGHT = 630;

/** Square publisher logo for JSON-LD Organization. */
export const SITE_LOGO = 'https://cdn.zoxo.kr/assets/logo.png';

/**
 * Canonical origin (no trailing slash). On the client we prefer the live origin;
 * on the server we use VITE_SITE_URL (set per deploy) or fall back to the
 * production domain.
 */
export const SITE_URL = (
  (typeof window !== 'undefined' && window.location?.origin) ||
  import.meta.env.VITE_SITE_URL ||
  'https://zoxo.kr'
).replace(/\/$/, '');

/** Build an absolute canonical URL from a path (e.g. "/surveys/abc"). */
export function canonical(path: string): string {
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}
